import { Accordion, Container, ContainerSize, Heading, Text } from "@rebootui";

import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";

type AccordionPageProps = {};

/** Use string arrays, not template literals with closing tags — TSX treats `</` inside backticks like JSX. */
const defaultAccordionCode = [
    "<Accordion>",
    '    <Accordion.Item title="Accordion Item #1">',
    "        <Text>",
    "            This is the first item's accordion body. It is shown by default, until the collapse plugin",
    "            adds the appropriate classes that we use to style each element. These classes control the",
    "            overall appearance, as well as the showing and hiding via CSS transitions. You can modify",
    "            any of this with custom CSS or overriding our default variables. It's also worth noting that",
    "            just about any HTML can go within the .accordion-body, though the transition does limit",
    "            overflow.",
    "        </Text>",
    "    </Accordion.Item>",
    '    <Accordion.Item title="Accordion Item #2">',
    "        <Text>",
    "            Another body. Food truck quinoa nesciunt laborum eiusmod. Brunch 3 wolf moon.",
    "        </Text>",
    "    </Accordion.Item>",
    '    <Accordion.Item title="Accordion Item #3">',
    "        <Text>",
    "            Another body. Food truck quinoa nesciunt laborum eiusmod. Brunch 3 wolf moon.",
    "        </Text>",
    "    </Accordion.Item>",
    "</Accordion>",
].join("\n");

const AccordionFlushCode = [
    '<div className="bg-dark bg-opacity-10 p-3">',
    "    <Accordion flush>",
    '        <Accordion.Item title="Flush item #1">',
    "            <Text>",
    "                Place a flush accordion inside a parent with a border or background so the lack of",
    "                outer accordion styling reads clearly.",
    "            </Text>",
    "        </Accordion.Item>",
    '        <Accordion.Item title="Flush item #2">',
    "            <Text>",
    "                Another body. Food truck quinoa nesciunt laborum eiusmod. Brunch 3 wolf moon.",
    "            </Text>",
    "        </Accordion.Item>",
    '        <Accordion.Item title="Flush item #3">',
    "            <Text>",
    "                Another body. Food truck quinoa nesciunt laborum eiusmod. Brunch 3 wolf moon.",
    "            </Text>",
    "        </Accordion.Item>",
    "    </Accordion>",
    "</div>",
].join("\n");

const AccordionStayOpenCode = [
    "<Accordion stayOpen={true}>",
    '    <Accordion.Item title="Accordion Item #1">',
    "        <Text>",
    "            This is the first item's accordion body. It is shown by default, until the collapse plugin",
    "            adds the appropriate classes that we use to style each element. These classes control the",
    "            overall appearance, as well as the showing and hiding via CSS transitions. You can modify",
    "            any of this with custom CSS or overriding our default variables. It's also worth noting that",
    "            just about any HTML can go within the .accordion-body, though the transition does limit",
    "            overflow.",
    "        </Text>",
    "    </Accordion.Item>",
    '    <Accordion.Item title="Accordion Item #2">',
    "        <Text>",
    "            Another body. Food truck quinoa nesciunt laborum eiusmod. Brunch 3 wolf moon.",
    "        </Text>",
    "    </Accordion.Item>",
    '    <Accordion.Item title="Accordion Item #3">',
    "        <Text>",
    "            Another body. Food truck quinoa nesciunt laborum eiusmod. Brunch 3 wolf moon.",
    "        </Text>",
    "    </Accordion.Item>",
    "</Accordion>",
].join("\n");

const AccordionInitialOpenCode = [
    "<Accordion>",
    '    <Accordion.Item eventKey="1" title="Starts open" open={true}>',
    "        <Text>This panel is expanded on first render. The open prop sets initial state only.</Text>",
    "    </Accordion.Item>",
    '    <Accordion.Item eventKey="2" title="Second item">',
    "        <Text>Another body.</Text>",
    "    </Accordion.Item>",
    '    <Accordion.Item eventKey="3" title="Third item">',
    "        <Text>Another body.</Text>",
    "    </Accordion.Item>",
    "</Accordion>",
].join("\n");

const AccordionPage = ({}: AccordionPageProps) => {
    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Accordion
            </Heading>

            <Text className="mb-4">
                The <Text el="strong">Accordion</Text> component groups collapsible sections. Each section is an{" "}
                <Text el="code">Accordion.Item</Text> with a <Text el="code">title</Text> and body content.
                The root supports <Text el="code">stayOpen</Text> (several panels open at once),{" "}
                <Text el="code">flush</Text> (Bootstrap <Text el="code">accordion-flush</Text>), and standard{" "}
                <Text el="code">id</Text> / <Text el="code">style</Text> / <Text el="code">className</Text> on the
                outer wrapper. Items support <Text el="code">open</Text> for an initially expanded panel,{" "}
                <Text el="code">eventKey</Text> for a stable panel id (recommended with <Text el="code">open</Text> or
                SSR), plus <Text el="code">className</Text> and the same div attributes on the item wrapper.
                Headers use <Text el="code">type="button"</Text>; the collapse region is exposed as a{" "}
                <Text el="code">role="region"</Text> with <Text el="code">aria-labelledby</Text> for accessibility.
            </Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Accordion (default behaviour)
            </Heading>
            <Text className="mb-4">
                Each section expands or collapses when its header is clicked. Only one section is open at a time
                unless you set <Text el="code">stayOpen</Text> on the root.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Accordion>
                        <Accordion.Item title="Accordion Item #1">
                            <Text>
                                This is the first item's accordion body. It is shown by default, until the collapse
                                plugin adds the appropriate classes that we use to style each element. These classes
                                control the overall appearance, as well as the showing and hiding via CSS transitions.
                                You can modify any of this with custom CSS or overriding our default variables. It's
                                also worth noting that just about any HTML can go within the .accordion-body, though
                                the transition does limit overflow.
                            </Text>
                        </Accordion.Item>
                        <Accordion.Item title="Accordion Item #2">
                            <Text>
                                Another body. Food truck quinoa nesciunt laborum eiusmod. Brunch 3 wolf moon.
                            </Text>
                        </Accordion.Item>
                        <Accordion.Item title="Accordion Item #3">
                            <Text>
                                Another body. Food truck quinoa nesciunt laborum eiusmod. Brunch 3 wolf moon.
                            </Text>
                        </Accordion.Item>
                    </Accordion>
                </CodePreview>
                <CodeHighlighter language="tsx">{defaultAccordionCode}</CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Initially open
            </Heading>
            <Text className="mb-4">
                Set <Text el="code">open</Text> on an item to expand it on the first render. This is{" "}
                <Text el="strong">initial state only</Text> (not a controlled prop): after mount, open/closed state
                follows user interaction. When you use <Text el="code">open</Text>, pass a stable{" "}
                <Text el="code">eventKey</Text> on each item so ids stay consistent (especially with SSR or React
                Strict Mode).
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Accordion>
                        <Accordion.Item eventKey="1" title="Starts open" open={true}>
                            <Text>
                                This panel is expanded on first render. The open prop sets initial state only.
                            </Text>
                        </Accordion.Item>
                        <Accordion.Item eventKey="2" title="Second item">
                            <Text>Another body.</Text>
                        </Accordion.Item>
                        <Accordion.Item eventKey="3" title="Third item">
                            <Text>Another body.</Text>
                        </Accordion.Item>
                    </Accordion>
                </CodePreview>
                <CodeHighlighter language="tsx">{AccordionInitialOpenCode}</CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Flush
            </Heading>
            <Text className="mb-4">
                Set <Text el="code">flush</Text> to remove the outer border and rounded corners so the accordion lines
                up edge-to-edge inside a card, modal, or other bordered container.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <div className="bg-dark bg-opacity-10 p-3">
                        <Accordion flush>
                            <Accordion.Item title="Flush item #1">
                                <Text>
                                    Place a flush accordion inside a parent with a border or background so the lack of
                                    outer accordion styling reads clearly.
                                </Text>
                            </Accordion.Item>
                            <Accordion.Item title="Flush item #2">
                                <Text>
                                    Another body. Food truck quinoa nesciunt laborum eiusmod. Brunch 3 wolf moon.
                                </Text>
                            </Accordion.Item>
                            <Accordion.Item title="Flush item #3">
                                <Text>
                                    Another body. Food truck quinoa nesciunt laborum eiusmod. Brunch 3 wolf moon.
                                </Text>
                            </Accordion.Item>
                        </Accordion>
                    </div>
                </CodePreview>
                <CodeHighlighter language="tsx">{AccordionFlushCode}</CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Stay Open
            </Heading>
            <Text className="mb-4">
                Set <Text el="code">stayOpen</Text> on <Text el="code">Accordion</Text> so more than one section can
                stay expanded at the same time. To start with specific panels open, combine <Text el="code">stayOpen</Text>{" "}
                with <Text el="code">open</Text> on the items you want expanded initially (each with an{" "}
                <Text el="code">eventKey</Text>).
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Accordion stayOpen={true}>
                        <Accordion.Item title="Accordion Item #1">
                            <Text>
                                This is the first item's accordion body. It is shown by default, until the collapse
                                plugin adds the appropriate classes that we use to style each element. These classes
                                control the overall appearance, as well as the showing and hiding via CSS transitions.
                                You can modify any of this with custom CSS or overriding our default variables. It's
                                also worth noting that just about any HTML can go within the .accordion-body, though
                                the transition does limit overflow.
                            </Text>
                        </Accordion.Item>
                        <Accordion.Item title="Accordion Item #2">
                            <Text>
                                Another body. Food truck quinoa nesciunt laborum eiusmod. Brunch 3 wolf moon.
                            </Text>
                        </Accordion.Item>
                        <Accordion.Item title="Accordion Item #3">
                            <Text>
                                Another body. Food truck quinoa nesciunt laborum eiusmod. Brunch 3 wolf moon.
                            </Text>
                        </Accordion.Item>
                    </Accordion>
                </CodePreview>
                <CodeHighlighter language="tsx">{AccordionStayOpenCode}</CodeHighlighter>
            </CodeSnippet>
        </Container>
    );
};

export default AccordionPage;
